import { apiFetch } from '@/api/authenticatedClient';

export const MEDIA_AVAILABLE = 'available';
export const MEDIA_UNAVAILABLE = 'unavailable';
export const MEDIA_FORBIDDEN = 'forbidden';
export const MEDIA_ERROR = 'error';
export const MEDIA_REASON_NOT_KEPT = 'not_kept';
export const MEDIA_REASON_DOWNLOADING = 'downloading';
export const MEDIA_REASON_UNAVAILABLE = 'unavailable';
export const MEDIA_REASON_UNVERIFIED = 'unverified';

const unavailableStatuses = new Set([404, 409]);
const forbiddenStatuses = new Set([401, 403]);
const unavailableReasons = new Set([
  MEDIA_REASON_NOT_KEPT,
  MEDIA_REASON_DOWNLOADING,
  MEDIA_REASON_UNAVAILABLE,
  MEDIA_REASON_UNVERIFIED,
]);

async function boundedUnavailableReason(response) {
  if (typeof response?.json !== 'function') return null;
  try {
    const body = await response.json();
    const detail = body?.detail;
    if (detail?.code !== 'media_offline_unavailable') return null;
    return unavailableReasons.has(detail.reason) ? detail.reason : null;
  } catch {
    return null;
  }
}

/**
 * Load one authenticated business-media URL without knowing whether the active
 * API is cloud or the protected desktop backend. Cancellation is represented
 * by AbortError; callers must not turn an obsolete request into visible error.
 */
export async function loadAuthenticatedMedia(url, { signal, headers } = {}) {
  try {
    const response = await apiFetch(url, { signal, ...(headers ? { headers } : {}) });
    if (response.ok) {
      const blob = await response.blob();
      if (!(blob instanceof Blob) || blob.size === 0) {
        return { state: MEDIA_ERROR };
      }
      return {
        state: MEDIA_AVAILABLE,
        blob,
        contentType: blob.type || response.headers?.get?.('content-type') || '',
      };
    }
    if (forbiddenStatuses.has(response.status)) return { state: MEDIA_FORBIDDEN };
    if (unavailableStatuses.has(response.status)) {
      const reason = await boundedUnavailableReason(response);
      if (reason === MEDIA_REASON_UNVERIFIED) return { state: MEDIA_ERROR, reason };
      return reason ? { state: MEDIA_UNAVAILABLE, reason } : { state: MEDIA_UNAVAILABLE };
    }
    return { state: MEDIA_ERROR };
  } catch (error) {
    if (error?.name === 'AbortError' || signal?.aborted) throw error;
    return { state: MEDIA_ERROR };
  }
}
